import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { MemberPageScaffold } from "@/components/MemberScaffold";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppFieldGroup,
  AppFormSection,
  AppInput,
  AppText,
  AppTextArea,
  kindredFontFamilies,
} from "../../ui-foundation";

const CreateChatRoom = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim() || !user || creating) return;

    setCreating(true);
    const { data: room, error } = await supabase
      .from("chat_rooms")
      .insert({ name: name.trim(), description: description.trim() || null, created_by: user.id })
      .select()
      .single();

    if (error || !room) {
      toast.error("Couldn't create room");
      setCreating(false);
      return;
    }

    await supabase.from("chat_room_members").insert({ room_id: room.id, user_id: user.id });
    toast.success("Room created");
    navigate(`/community/room/${room.id}`);
  };

  return (
    <MemberPageScaffold
      title="New Chat Room"
      subtitle="Create a focused space for one shared topic inside Community."
      fallbackPath="/communities?section=rooms"
      action={
        <AppButton
          size="sm"
          onPress={handleCreate}
          disabled={!name.trim() || creating}
        >
          {creating ? "Creating..." : "Create"}
        </AppButton>
      }
    >
      <AppCard>
        <AppCardBody gap="$md">
          <AppFormSection
            title="Room details"
            description="Keep the name clear so members know exactly what this room is for."
          >
            <AppFieldGroup
              label="Room name"
              helper={`${name.length}/50`}
            >
              <AppInput
                value={name}
                onChangeText={(value) => setName(value.slice(0, 50))}
                placeholder="Give your room a name"
                maxLength={50}
              />
            </AppFieldGroup>

            <AppFieldGroup
              label="Description"
              helper={`${description.length}/200`}
            >
              <AppTextArea
                value={description}
                onChangeText={(value) => setDescription(value.slice(0, 200))}
                placeholder="What's this room for?"
                maxLength={200}
                minHeight={120}
              />
            </AppFieldGroup>
          </AppFormSection>
        </AppCardBody>
      </AppCard>

      <AppCard borderStyle="dashed">
        <AppCardBody gap="$xs">
          <MessageSquarePlus size={20} className="text-primary" />
          <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm">
            Start with a simple purpose
          </AppText>
          <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
            Rooms work best when members can tell at a glance whether they should join.
          </AppText>
        </AppCardBody>
      </AppCard>
    </MemberPageScaffold>
  );
};

export default CreateChatRoom;
